import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '@/context/AdminContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminService } from '@/services/adminApi';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { 
  MessageSquare, RefreshCw, Trash2, ChevronLeft, ChevronRight,
  Clock, User, Menu, Eye, Smile, Meh, Frown, Target,
  TrendingUp, CheckCircle, AlertCircle, XCircle, Activity, Sparkles, Loader2,
  ArrowUpDown, ArrowUp, ArrowDown, Send, Bot, X, Maximize2, Minimize2,
  Download, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSessions() {
  const { isAuthenticated, loading: authLoading } = useAdmin();
  const { colors, isDark } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sentimentStats, setSentimentStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingAISummary, setLoadingAISummary] = useState({});
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // AI Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  
  const limit = 50; // Increased for better AI analysis

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSessions();
      loadSentimentStats();
    }
  }, [isAuthenticated, page]);

  // Sort sessions when sortConfig or sessions change
  useEffect(() => {
    if (sessions.length > 0) {
      const sorted = sortSessions(sessions, sortConfig);
      setFilteredSessions(sorted);
    }
  }, [sessions, sortConfig]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const res = await adminService.getSessionsWithSentiment({ limit, offset: page * limit });
      setSessions(res.data);
      setFilteredSessions(res.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      try {
        const res = await adminService.getSessions({ limit, offset: page * limit });
        setSessions(res.data);
        setFilteredSessions(res.data);
      } catch (e) {
        toast.error('Erreur lors du chargement des sessions');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSentimentStats = async () => {
    try {
      setLoadingStats(true);
      const res = await adminService.getSentimentStats();
      setSentimentStats(res.data);
    } catch (error) {
      console.error('Error loading sentiment stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Sorting functions
  const sortSessions = (data, config) => {
    const sorted = [...data].sort((a, b) => {
      let aVal = a[config.key];
      let bVal = b[config.key];
      
      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Handle dates
      if (config.key === 'created_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return config.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle strings
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (config.direction === 'asc') {
        return aStr.localeCompare(bStr);
      }
      return bStr.localeCompare(aStr);
    });
    
    return sorted;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-3 h-3 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-[#5BA946]" />
      : <ArrowDown className="w-3 h-3 text-[#5BA946]" />;
  };

  // AI Chat functions
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const res = await adminService.querySessionsAI({ 
        query: userMessage,
        sessions_data: sessions.map(s => ({
          id: s.id,
          user: s.profile_name || 'Anonyme',
          sentiment: s.sentiment,
          outcome: s.outcome,
          engagement: s.engagement_score,
          messages: s.message_count,
          summary: s.summary,
          date: s.created_at,
          entry_type: s.entry_type
        }))
      });
      
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response 
      }]);
    } catch (error) {
      console.error('AI Chat error:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Désolé, je n'ai pas pu traiter votre demande. Veuillez réessayer." 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette session ?')) return;
    try {
      await adminService.deleteSession(sessionId);
      toast.success('Session supprimée');
      loadSessions();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleGetAISummary = async (sessionId) => {
    setLoadingAISummary(prev => ({ ...prev, [sessionId]: true }));
    try {
      const res = await adminService.getSessionAISummary(sessionId);
      const aiSummary = res.data.summary;
      setSessions(prev => prev.map(s => 
        s.id === sessionId ? { ...s, summary: aiSummary, is_ai_summary: true } : s
      ));
      toast.success('Résumé AI généré');
    } catch (error) {
      console.error('Error getting AI summary:', error);
      toast.error('Erreur lors de la génération du résumé AI');
    } finally {
      setLoadingAISummary(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const getEntryTypeBadge = (type) => {
    const styles = {
      create: 'bg-green-500/20 text-green-500',
      improve: 'bg-orange-500/20 text-orange-500',
      upload: 'bg-blue-500/20 text-blue-500',
      convert: 'bg-purple-500/20 text-purple-500'
    };
    const labels = {
      create: 'Création',
      improve: 'Amélioration',
      upload: 'Upload',
      convert: 'Conversion'
    };
    return (
      <Badge className={styles[type] || 'bg-slate-500/20 text-slate-400'}>
        {labels[type] || type || 'Inconnu'}
      </Badge>
    );
  };

  const getSentimentBadge = (sentiment) => {
    const config = {
      positive: { icon: Smile, color: 'bg-green-500/20 text-green-500', label: 'Positif' },
      neutral: { icon: Meh, color: 'bg-yellow-500/20 text-yellow-500', label: 'Neutre' },
      negative: { icon: Frown, color: 'bg-red-500/20 text-red-500', label: 'Négatif' }
    };
    const { icon: Icon, color, label } = config[sentiment] || config.neutral;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getOutcomeBadge = (outcome) => {
    const config = {
      completed: { icon: CheckCircle, color: 'bg-green-500/20 text-green-500', label: 'Complété' },
      in_progress: { icon: Activity, color: 'bg-blue-500/20 text-blue-500', label: 'En cours' },
      abandoned: { icon: XCircle, color: 'bg-red-500/20 text-red-500', label: 'Abandonné' },
      exploring: { icon: Target, color: 'bg-purple-500/20 text-purple-500', label: 'Exploration' }
    };
    const { icon: Icon, color, label } = config[outcome] || { icon: AlertCircle, color: 'bg-slate-500/20 text-slate-400', label: outcome || 'Inconnu' };
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  // Sortable column header component
  const SortableHeader = ({ label, sortKey, className = '' }) => (
    <th 
      className={`text-left p-4 ${colors.textSecondary} font-medium text-sm cursor-pointer hover:text-[#5BA946] transition-colors select-none ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        {getSortIcon(sortKey)}
      </div>
    </th>
  );

  if (authLoading) {
    return (
      <div className={`min-h-screen ${colors.bg} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-[#5BA946] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${colors.bg} flex`}>
      <AdminSidebar 
        currentPath={location.pathname}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className={`${colors.bgHeader} border-b ${colors.border} p-4 sticky top-0 z-10`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setSidebarOpen(true)}
                variant="ghost"
                size="icon"
                className={`lg:hidden ${colors.textSecondary}`}
              >
                <Menu className="w-6 h-6" />
              </Button>
              <div>
                <h1 className={`text-xl font-bold ${colors.text}`}>💬 Sessions Chat</h1>
                <p className={`${colors.textSecondary} text-sm mt-1`}>
                  Analyse des conversations • Cliquez sur les colonnes pour trier
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setChatOpen(true)} 
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white gap-2"
              >
                <Bot className="w-4 h-4" />
                Analyser avec IA
              </Button>
              <Button 
                onClick={() => { loadSessions(); loadSentimentStats(); }} 
                variant="outline" 
                size="sm"
                className={colors.btnOutline}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 space-y-4">
          {/* Sentiment Stats Cards */}
          {sentimentStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className={`${colors.bgCard} ${colors.border}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`${colors.textMuted} text-sm`}>Taux de Satisfaction</p>
                      <p className={`text-2xl font-bold ${colors.text}`}>
                        {sentimentStats.satisfaction_rate}%
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Smile className="w-6 h-6 text-green-500" />
                    </div>
                  </div>
                  <Progress value={sentimentStats.satisfaction_rate} className="mt-3 h-2" />
                </CardContent>
              </Card>

              <Card className={`${colors.bgCard} ${colors.border}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`${colors.textMuted} text-sm`}>Taux de Complétion</p>
                      <p className={`text-2xl font-bold ${colors.text}`}>
                        {sentimentStats.completion_rate}%
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-blue-500" />
                    </div>
                  </div>
                  <Progress value={sentimentStats.completion_rate} className="mt-3 h-2" />
                </CardContent>
              </Card>

              <Card className={`${colors.bgCard} ${colors.border}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`${colors.textMuted} text-sm`}>Engagement Moyen</p>
                      <p className={`text-2xl font-bold ${colors.text}`}>
                        {sentimentStats.average_engagement}/100
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-500" />
                    </div>
                  </div>
                  <Progress value={sentimentStats.average_engagement} className="mt-3 h-2" />
                </CardContent>
              </Card>

              <Card className={`${colors.bgCard} ${colors.border}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`${colors.textMuted} text-sm`}>Sessions Analysées</p>
                      <p className={`text-2xl font-bold ${colors.text}`}>
                        {sentimentStats.total_analyzed}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-orange-500" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Badge className="bg-green-500/20 text-green-500 text-xs">
                      😊 {sentimentStats.sentiment_counts?.positive || 0}
                    </Badge>
                    <Badge className="bg-yellow-500/20 text-yellow-500 text-xs">
                      😐 {sentimentStats.sentiment_counts?.neutral || 0}
                    </Badge>
                    <Badge className="bg-red-500/20 text-red-500 text-xs">
                      😞 {sentimentStats.sentiment_counts?.negative || 0}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Sessions Table */}
          <Card className={`${colors.bgCard} ${colors.border}`}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${colors.border}`}>
                      <SortableHeader label="Utilisateur" sortKey="profile_name" />
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium text-sm`}>Résumé</th>
                      <SortableHeader label="Sentiment" sortKey="sentiment" />
                      <SortableHeader label="Résultat" sortKey="outcome" />
                      <SortableHeader label="Engagement" sortKey="engagement_score" />
                      <SortableHeader label="Messages" sortKey="message_count" />
                      <SortableHeader label="CV" sortKey="cv_downloaded" />
                      <SortableHeader label="App" sortKey="app_exported" />
                      <SortableHeader label="Date" sortKey="created_at" />
                      <th className={`text-right p-4 ${colors.textSecondary} font-medium text-sm`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={10} className={`p-8 text-center ${colors.textMuted}`}>
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-[#5BA946] border-t-transparent rounded-full animate-spin"></div>
                            Chargement...
                          </div>
                        </td>
                      </tr>
                    ) : filteredSessions.length === 0 ? (
                      <tr>
                        <td colSpan={10} className={`p-8 text-center ${colors.textMuted}`}>
                          Aucune session trouvée
                        </td>
                      </tr>
                    ) : filteredSessions.map((session) => (
                      <tr 
                        key={session.id} 
                        className={`border-b ${colors.border} ${colors.bgHover} transition-colors`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#5BA946]/20 flex items-center justify-center">
                              <User className="w-4 h-4 text-[#5BA946]" />
                            </div>
                            <div>
                              <p className={`${colors.text} font-medium`}>
                                {session.profile_name || 'Anonyme'}
                              </p>
                              <p className={`${colors.textMuted} text-xs`}>
                                {getEntryTypeBadge(session.entry_type)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 max-w-md">
                          <div className="flex items-start gap-2">
                            <div className={`${colors.textSecondary} text-sm leading-relaxed flex-1`}>
                              {session.summary || "Pas de résumé disponible"}
                            </div>
                            {!session.is_ai_summary && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleGetAISummary(session.id)}
                                disabled={loadingAISummary[session.id]}
                                className={`${colors.textMuted} hover:text-[#5BA946] flex-shrink-0`}
                                title="Générer résumé AI"
                              >
                                {loadingAISummary[session.id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Sparkles className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {session.is_ai_summary && (
                              <Badge className="bg-purple-500/20 text-purple-400 text-xs flex-shrink-0">
                                AI
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          {getSentimentBadge(session.sentiment)}
                        </td>
                        <td className="p-4">
                          {getOutcomeBadge(session.outcome)}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Progress 
                              value={session.engagement_score || 0} 
                              className="w-16 h-2"
                            />
                            <span className={`${colors.textMuted} text-xs`}>
                              {session.engagement_score || 0}%
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <MessageSquare className={`w-4 h-4 ${colors.textMuted}`} />
                            <span className={colors.text}>{session.message_count || 0}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center">
                            {session.cv_downloaded ? (
                              <div className="flex items-center gap-1 text-green-500" title={session.cv_downloaded_at ? `Téléchargé le ${new Date(session.cv_downloaded_at).toLocaleDateString('fr-FR')}` : 'Téléchargé'}>
                                <Download className="w-4 h-4" />
                                <CheckCircle className="w-3 h-3" />
                              </div>
                            ) : (
                              <XCircle className="w-4 h-4 text-gray-400" title="Non téléchargé" />
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center">
                            {session.app_exported ? (
                              <div className="flex items-center gap-1 text-purple-500" title={session.app_exported_at ? `Exporté le ${new Date(session.app_exported_at).toLocaleDateString('fr-FR')}` : 'Exporté vers App'}>
                                <Smartphone className="w-4 h-4" />
                                <CheckCircle className="w-3 h-3" />
                              </div>
                            ) : (
                              <XCircle className="w-4 h-4 text-gray-400" title="Non exporté" />
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className={`${colors.textSecondary} text-sm`}>
                            {session.created_at 
                              ? new Date(session.created_at).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : '-'}
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedSession(session)}
                              className={`${colors.textSecondary} hover:text-blue-500`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(session.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className={`flex items-center justify-between p-4 border-t ${colors.border}`}>
                <p className={`text-sm ${colors.textSecondary}`}>
                  Page {page + 1} • {filteredSessions.length} résultats
                  {sortConfig.key && (
                    <span className="ml-2 text-[#5BA946]">
                      (trié par {sortConfig.key === 'profile_name' ? 'utilisateur' : 
                        sortConfig.key === 'created_at' ? 'date' :
                        sortConfig.key === 'engagement_score' ? 'engagement' :
                        sortConfig.key === 'message_count' ? 'messages' : sortConfig.key})
                    </span>
                  )}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className={colors.btnOutline}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={filteredSessions.length < limit}
                    onClick={() => setPage(p => p + 1)}
                    className={colors.btnOutline}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Session Detail Modal */}
        {selectedSession && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`${colors.bgCard} ${colors.border} max-w-2xl w-full max-h-[80vh] overflow-hidden`}>
              <div className={`p-4 border-b ${colors.border} flex items-center justify-between`}>
                <div>
                  <h3 className={`${colors.text} font-semibold`}>Détails de la session</h3>
                  <p className={`${colors.textMuted} text-sm`}>
                    {selectedSession.profile_name || 'Anonyme'} • {selectedSession.message_count || 0} messages
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedSession(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="space-y-4">
                  <div className={`p-4 ${isDark ? 'bg-gradient-to-r from-slate-700/50 to-slate-600/30' : 'bg-gradient-to-r from-gray-100 to-gray-50'} rounded-lg border-l-4 border-[#5BA946]`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className={`text-xs ${colors.textMuted} uppercase tracking-wide font-medium`}>
                        Résumé de la conversation
                      </p>
                      {!selectedSession.is_ai_summary && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleGetAISummary(selectedSession.id)}
                          disabled={loadingAISummary[selectedSession.id]}
                          className="text-purple-400 hover:text-purple-300 -mt-1"
                        >
                          {loadingAISummary[selectedSession.id] ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-1" />
                          )}
                          Résumé AI
                        </Button>
                      )}
                    </div>
                    <p className={`${colors.text} leading-relaxed`}>
                      {selectedSession.summary || "Pas de résumé disponible"}
                    </p>
                    {selectedSession.is_ai_summary && (
                      <Badge className="bg-purple-500/20 text-purple-400 text-xs mt-2">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Généré par IA
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                      <p className={`text-xs ${colors.textMuted} mb-1`}>Type d'entrée</p>
                      {getEntryTypeBadge(selectedSession.entry_type)}
                    </div>
                    <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                      <p className={`text-xs ${colors.textMuted} mb-1`}>Sentiment</p>
                      {getSentimentBadge(selectedSession.sentiment)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                      <p className={`text-xs ${colors.textMuted} mb-1`}>Résultat</p>
                      {getOutcomeBadge(selectedSession.outcome)}
                    </div>
                    <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                      <p className={`text-xs ${colors.textMuted} mb-1`}>Engagement</p>
                      <div className="flex items-center gap-2">
                        <Progress value={selectedSession.engagement_score || 0} className="flex-1 h-2" />
                        <span className={colors.text}>{selectedSession.engagement_score || 0}%</span>
                      </div>
                    </div>
                  </div>
                  <div className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                    <p className={`text-xs ${colors.textMuted} mb-1`}>Créée le</p>
                    <p className={colors.text}>
                      {selectedSession.created_at 
                        ? new Date(selectedSession.created_at).toLocaleString('fr-FR')
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* AI Chat Panel */}
        {chatOpen && (
          <div className={`fixed ${chatExpanded ? 'inset-4' : 'bottom-4 right-4 w-96 h-[500px]'} z-50 transition-all duration-300`}>
            <Card className={`${colors.bgCard} ${colors.border} h-full flex flex-col shadow-2xl`}>
              {/* Chat Header */}
              <div className={`p-4 border-b ${colors.border} flex items-center justify-between bg-gradient-to-r from-purple-500/10 to-pink-500/10`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className={`${colors.text} font-semibold text-sm`}>Assistant Analytique</h3>
                    <p className={`${colors.textMuted} text-xs`}>Posez vos questions sur les sessions</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChatExpanded(!chatExpanded)}
                    className={colors.textSecondary}
                  >
                    {chatExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChatOpen(false)}
                    className={colors.textSecondary}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.length === 0 && (
                  <div className={`text-center ${colors.textMuted} py-8`}>
                    <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm mb-4">Posez-moi des questions sur vos sessions !</p>
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setChatInput("Quels sont les principaux problèmes rencontrés par les utilisateurs ?")}
                        className={`${colors.btnOutline} text-xs w-full justify-start`}
                      >
                        💡 Quels sont les principaux problèmes ?
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setChatInput("Pourquoi les utilisateurs abandonnent-ils leurs sessions ?")}
                        className={`${colors.btnOutline} text-xs w-full justify-start`}
                      >
                        💡 Pourquoi les abandons ?
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setChatInput("Quelles améliorations suggérez-vous pour le système ?")}
                        className={`${colors.btnOutline} text-xs w-full justify-start`}
                      >
                        💡 Suggestions d'amélioration ?
                      </Button>
                    </div>
                  </div>
                )}
                
                {chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg p-3 ${
                        msg.role === 'user'
                          ? 'bg-[#5BA946] text-white'
                          : isDark ? 'bg-slate-700' : 'bg-gray-100'
                      }`}
                    >
                      <p className={`text-sm whitespace-pre-wrap ${msg.role === 'assistant' ? colors.text : ''}`}>
                        {msg.content}
                      </p>
                    </div>
                  </div>
                ))}
                
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className={`rounded-lg p-3 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center gap-2">
                        <Loader2 className={`w-4 h-4 animate-spin ${colors.textMuted}`} />
                        <span className={`text-sm ${colors.textMuted}`}>Analyse en cours...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <form onSubmit={handleChatSubmit} className={`p-4 border-t ${colors.border}`}>
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Posez une question sur les sessions..."
                    className={`flex-1 ${isDark ? 'bg-slate-700 border-slate-600' : ''}`}
                    disabled={chatLoading}
                  />
                  <Button
                    type="submit"
                    disabled={chatLoading || !chatInput.trim()}
                    className="bg-[#5BA946] hover:bg-[#4a9339]"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
